import React, { useEffect } from 'react'
import {
	change, Field, FieldArray, getFormValues, InjectedFormProps, reduxForm
} from 'redux-form'
import {
	Button, Col, Divider, Form, Modal, notification, Popconfirm, Row, Select, Tag
} from 'antd'
import {
	ArrowRightOutlined, CrownFilled, DeleteOutlined, EyeOutlined, PlusCircleOutlined, PlusOutlined, QuestionCircleOutlined, SaveOutlined, SendOutlined, UnorderedListOutlined
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { map } from 'lodash'
import i18next from 'i18next'
import dayjs from 'dayjs'
import {
	DEFAULT_DATE_WITH_TIME_FORMAT, FORMS, PRICE_OFFER_STATUS, translatePriceOfferStatusColor, translatePriceOfferStatusToSk
} from '../../../utils/enums'
import TextField from '../../../atoms/form/TextField'
import validateForm from './validatePriceOfferForm'
import {
	deletePriceOffer, getPriceOfferHeaderOptions, getPriceOfferSentEmails, IFirmHeader, IOtherPriceOffer, IPriceOfferForm, IPriceOfferSentEmail, updatePriceOfferHousingAssociation
} from '../../../reducers/priceOffers/actions'
import SentEmailsDrawer from './SentEmailsDrawer'
import OtherPriceOffersDrawer from './OtherPriceOffersDrawer'
import InputField from '../../../atoms/InputField'
import SwitchField from '../../../atoms/SwitchField'
import DateField from '../../../atoms/DateField'
import SelectField from '../../../atoms/SelectField'
import { RootState } from '../../../reducers'
import { getTasks } from '../../../reducers/tasks/actions'
import { FREQUENCY } from '../../../utils/translate'
import InputNumberField from '../../../atoms/InputNumberField'
import ConfirmationModal from './FrequencyPickerModal'
import FrequencyJobsField from './FrequencyJobsField'
import StyledLink from '../../../components/NavLink'
import CustomOptionsSelectField from '../../../atoms/form/CustomOptionsSelectField'
import EmailPickerModal from './EmailPickerModal'
import { history } from '../../../utils/history'
import {
	createHousingAssociation, getHousingAssociations, IHousingAssociation
} from '../../../reducers/housingAssociations/actions'
import HousingAssociationModal from '../../housingAssociations/HousingAssociationModal'
import PriceOfferRemindersFields from './PriceOfferRemindersFields'

const { Option } = Select

type ComponentProps = {
	isCreate: boolean,
	id: number | null,
	sendPriceOffer: (id: number, additionalEmails: string[], inflationClauseDocumentSlot: 1 | 2 | null) => void
	showPdfPriceOffer: (id: number) => void
	showPdfPriceOfferPremium: (id: number) => void
	transferOfferToJob: (id: number) => void
	duplicatePriceOffer: (id: number) => void
	onRemove: (id: number) => void,
}

enum PlaceWithWaterOption {
	ROOM_FOR_CLEANING_SERVICE = 'Miestnosť pre upratovačku',
	HYDRANT = 'Hydrant',
}
enum PlaceToSpillWaterOption {
	ROOM_FOR_CLEANING_SERVICE = 'Miestnosť pre upratovačku',
	OUTSIDE = 'Vonku',
}

type Props = InjectedFormProps<IPriceOfferForm, ComponentProps> & ComponentProps

export const getPriceOfferTag = (status: PRICE_OFFER_STATUS) => <Tag
	color={translatePriceOfferStatusColor(status)}
>
	{translatePriceOfferStatusToSk(status)}
</Tag>

const PriceOfferForm = (props: Props) => {
	const dispatch = useDispatch()
	const {
		handleSubmit,
		invalid,
		pristine,
		isCreate,
		id,
		transferOfferToJob,
		duplicatePriceOffer,
		sendPriceOffer,
		showPdfPriceOffer,
		showPdfPriceOfferPremium,
		onRemove
	} = props

	const priceOfferStatusSelected: PRICE_OFFER_STATUS | null = (useSelector((state: RootState) => getFormValues(FORMS.PRICE_OFFER_FORM)(state)) as any)?.status
	const priceForm = (useSelector((state: RootState) => getFormValues(FORMS.PRICE_OFFER_FORM)(state)) as any)

	const detail = useSelector((state: RootState) => state.priceOffer.detail)

	// prihlaseny pouzivatel - predvyplneny prijemca novej pripomienky
	const currentUserId = useSelector((state: RootState) => state.profile.profile.data?.id) || null

	const [selectedFrequencies, setSelectedFrequencies] = React.useState<FREQUENCY[]>([])
	const [additionalPriceOfferEmails, setAdditionalPriceOfferEmails] = React.useState<string[]>([])

	const [isModalVisible, setIsModalVisible] = React.useState(false)

	const [isModalEmailPickerVisible, setModalEmailPickerVisible] = React.useState(false)

	const [headerOptions, setHeaderOptions] = React.useState<IFirmHeader[]>([])

	const [housingAssociations, setHousingAssociations] = React.useState<IHousingAssociation[]>([])
	const [isHousingAssociationModalVisible, setIsHousingAssociationModalVisible] = React.useState(false)
	const [isCreatingHousingAssociation, setIsCreatingHousingAssociation] = React.useState(false)

	const [isSentEmailsDrawerVisible, setIsSentEmailsDrawerVisible] = React.useState(false)
	const [sentEmails, setSentEmails] = React.useState<IPriceOfferSentEmail[]>([])

	const [isOtherPriceOffersDrawerVisible, setIsOtherPriceOffersDrawerVisible] = React.useState(false)

	const priceFormFrequenciesWithTasks = () => {
		const freqencies = []
		if (priceForm?.mondayTasks?.length > 0) { freqencies.push(FREQUENCY.MONDAY) }
		if (priceForm?.tuesdayTasks?.length > 0) { freqencies.push(FREQUENCY.TUESDAY) }
		if (priceForm?.wednesdayTasks?.length > 0) { freqencies.push(FREQUENCY.WEDNESDAY) }
		if (priceForm?.thursdayTasks?.length > 0) { freqencies.push(FREQUENCY.THURSDAY) }
		if (priceForm?.fridayTasks?.length > 0) { freqencies.push(FREQUENCY.FRIDAY) }
		if (priceForm?.saturdayTasks?.length > 0) { freqencies.push(FREQUENCY.SATURDAY) }
		if (priceForm?.sundayTasks?.length > 0) { freqencies.push(FREQUENCY.SUNDAY) }
		if (priceForm?.monthTasks?.length > 0) { freqencies.push(FREQUENCY.MONTH) }
		if (priceForm?.quarterYearTasks?.length > 0) { freqencies.push(FREQUENCY.QUARTERYEAR) }
		if (priceForm?.halfYearTasks?.length > 0) { freqencies.push(FREQUENCY.HALFYEAR) }
		if (priceForm?.yearTasks?.length > 0) { freqencies.push(FREQUENCY.YEAR) }
		return freqencies
	}

	const { t } = useTranslation('components')

	useEffect(() => {
		dispatch(getTasks(
			1,
			10000
		))
	}, [])

	useEffect(() => {
		dispatch(getPriceOfferHeaderOptions(
			(data) => {
				setHeaderOptions(data.headers)
			}
		))
	}, [])

	const loadHousingAssociations = (onLoaded?: (data: IHousingAssociation[]) => void) => {
		dispatch(getHousingAssociations((data) => {
			const list = Array.isArray(data) ? data : []
			setHousingAssociations(list)
			onLoaded?.(list)
		}))
	}

	useEffect(() => {
		loadHousingAssociations()
	}, [])

	useEffect(() => {
		if (selectedFrequencies.length === 0) {
			setSelectedFrequencies(Array.from(new Set(priceFormFrequenciesWithTasks())))
		}
	}, [priceForm])

	const fetchSentEmails = () => {
		if (id != null) {
			dispatch(getPriceOfferSentEmails(id, (data) => {
				setSentEmails(data)
			}))
		}
	}

	useEffect(() => {
		fetchSentEmails()
	}, [id])

	const priceOfferStatus = [
		{
			label: getPriceOfferTag(PRICE_OFFER_STATUS.NOT_ACCEPTED),
			value: PRICE_OFFER_STATUS.NOT_ACCEPTED
		},
		{
			label: getPriceOfferTag(PRICE_OFFER_STATUS.DECLINED),
			value: PRICE_OFFER_STATUS.DECLINED
		},
		{
			label: getPriceOfferTag(PRICE_OFFER_STATUS.FINISHED),
			value: PRICE_OFFER_STATUS.FINISHED
		},
		{
			label: getPriceOfferTag(PRICE_OFFER_STATUS.PRICE_INCREASE),
			value: PRICE_OFFER_STATUS.PRICE_INCREASE
		},
		{
			label: getPriceOfferTag(PRICE_OFFER_STATUS.PRICE_INCREASE_FINISHED),
			value: PRICE_OFFER_STATUS.PRICE_INCREASE_FINISHED
		},
		{
			label: getPriceOfferTag(PRICE_OFFER_STATUS.PRICE_INCREASE_DECLINED),
			value: PRICE_OFFER_STATUS.PRICE_INCREASE_DECLINED
		}
	]

	const placeWithWaterOptions = [
		{
			label: PlaceWithWaterOption.ROOM_FOR_CLEANING_SERVICE,
			value: PlaceWithWaterOption.ROOM_FOR_CLEANING_SERVICE
		},
		{
			label: PlaceWithWaterOption.HYDRANT,
			value: PlaceWithWaterOption.HYDRANT
		}
	]

	const placesToSpillWaterOptions = [
		{
			label: PlaceToSpillWaterOption.ROOM_FOR_CLEANING_SERVICE,
			value: PlaceToSpillWaterOption.ROOM_FOR_CLEANING_SERVICE
		},
		{
			label: PlaceToSpillWaterOption.OUTSIDE,
			value: PlaceToSpillWaterOption.OUTSIDE
		}
	]

	const priceOfferOptions = (headerOptions)?.map((option: IFirmHeader) => ({
		label: option.firmName,
		value: option.id
	})) || []

	const housingAssociationOptions = (housingAssociations)?.map((option: IHousingAssociation) => ({
		label: option.name,
		value: option.id
	})) || []

	const selectedHousingAssociation = housingAssociations.find((ha) => ha.id === priceForm?.housingAssociationID)

	// prilohy inflacnej dolozky vybraneho druzstva (pre dialog odoslania)
	const inflationClauseDocuments: { slot: 1 | 2, label: string }[] = selectedHousingAssociation
		? [
			...(selectedHousingAssociation.hasDocument1
				? [{ slot: 1 as const, label: selectedHousingAssociation.document1Label || selectedHousingAssociation.document1FileName || t('components:Príloha 1') }]
				: []),
			...(selectedHousingAssociation.hasDocument2
				? [{ slot: 2 as const, label: selectedHousingAssociation.document2Label || selectedHousingAssociation.document2FileName || t('components:Príloha 2') }]
				: [])
		]
		: []

	const isInflationClauseActive = Boolean(priceForm?.isInflationClauseIsPartOfPriceOffer)

	const handleCreateHousingAssociation = (name: string) => {
		setIsCreatingHousingAssociation(true)
		dispatch(createHousingAssociation(
			name,
			(created) => {
				setIsCreatingHousingAssociation(false)
				setIsHousingAssociationModalVisible(false)
				notification.success({ message: t('components:Bytové družstvo bolo vytvorené') })
				loadHousingAssociations(() => {
					dispatch(change(FORMS.PRICE_OFFER_FORM, 'housingAssociationID', created.id))
				})
			},
			() => setIsCreatingHousingAssociation(false)
		))
	}

	const isDuplicateDisabled = !(pristine || invalid) || id == null
	const otherPriceOffers = detail?.data?.otherPriceOffers || []
	const allPriceOffers: IOtherPriceOffer[] = (detail?.data?.id != null
		? [{
			id: detail.data.id,
			status: detail.data.status,
			price: detail.data.price,
			createdAt: detail.data.createdAt,
			updatedAt: detail.data.updatedAt,
			authorName: detail.data.authorName
		}, ...otherPriceOffers]
		: otherPriceOffers
	).slice().sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())

	// @ts-ignore
	return (
		<>
			<Form layout={'vertical'} onSubmitCapture={handleSubmit}>
				<Row justify={'end'} gutter={16}>
					<Col span={18} className={'grid'}>
						<div className={'flex direction-col justify-start sidebar-content'}>
							<Field
								name={'status'}
								component={SelectField}
								options={priceOfferStatus}
								label={t('components:Status')}
							/>
							{(id != null) && <div className={'mb-2'}>
								<div className={'font-semibold'}>{t('components:Autor cenovej ponuky')}</div>
								<div>{detail?.data?.authorName || '-'}</div>
							</div>
							}
							{(priceOfferStatusSelected !== PRICE_OFFER_STATUS.FINISHED && id != null) && <div className={'mt-2 mb-2 flex flex-row gap-2'}>
								<Button
									icon={<SendOutlined />}
									onClick={() => { setModalEmailPickerVisible(true) }}
									disabled={!(pristine || invalid) || detail?.data?.emailOfTrustee === null}
									type={'primary'}
									style={{ margin: '0px', flex: 1 }}
								>
									{detail?.data?.emailOfTrustee
										? <span>{t('pages:Odoslať cenovú ponuku (presunúť do stavu "Ukončená")')} + <b>{`na  ${detail?.data?.emailOfTrustee}`}</b></span>
										: <span>{t('pages:Odoslať cenovú ponuku (presunúť do stavu "Ukončená")')}</span>}
								</Button>
								<Button
									icon={<UnorderedListOutlined />}
									onClick={() => { fetchSentEmails(); setIsSentEmailsDrawerVisible(true) }}
									type={'default'}
									style={{ margin: '0px' }}
								>
									{sentEmails.length} {t('pages:Odoslané emaily')}
								</Button>
							</div>
							}
							{(priceOfferStatusSelected === PRICE_OFFER_STATUS.FINISHED && id != null) && <div className={'mt-2 mb-2 flex flex-row gap-2'}>
								<Button
									icon={<SendOutlined />}
									onClick={() => { setModalEmailPickerVisible(true) }}
									disabled={!(pristine || invalid)}
									type={'primary'}
									style={{ margin: '0px', flex: 1 }}
								>
									{`${t('pages:Odoslať opäť cenovú ponuku na email')}`} <b>{`  ${detail?.data?.emailOfTrustee}`}</b>
								</Button>
								<Button
									icon={<UnorderedListOutlined />}
									onClick={() => { fetchSentEmails(); setIsSentEmailsDrawerVisible(true) }}
									type={'default'}
									style={{ margin: '0px' }}
								>
									{sentEmails.length} {t('pages:Odoslané emaily')}
								</Button>
							</div>
							}
							{(id != null) && <div className={'mt-2 mb-2 flex flex-row gap-2'}>
								<Button
									icon={<EyeOutlined />}
									onClick={() => {
										if (id !== null) {
											showPdfPriceOffer(id)
										}
									}}
									disabled={!(pristine || invalid)}
									type={'default'}
									style={{ margin: '0px', flex: 1 }}
								>
									{t('pages:Zobraziť Základnú ponuku')}
								</Button>
								<Button
									icon={<CrownFilled />}
									onClick={() => {
										if (id !== null) {
											showPdfPriceOfferPremium(id)
										}
									}}
									disabled={!(pristine || invalid)}
									style={{
										margin: '0px',
										flex: 1,
										backgroundColor: '#d4af37',
										borderColor: '#d4af37',
										color: '#ffffff',
										fontWeight: 600
									}}
								>
									{t('pages:Zobraziť Premiovú ponuku')}
								</Button>
							</div>
							}
						</div>
					</Col>
					<Col span={6} className={'grid'}>
						<div className={'flex direction-col justify-between sidebar-content'}>
							<div>
								{!isCreate && (
									<Field
										name={'updatedAt'}
										component={TextField}
										disabled
										label={t('components:Dátum poslednej úpravy')}
									/>
								)}

								<div className={'flex direction-col justify-center'}>
									<Button
										icon={<SaveOutlined />}
										onClick={handleSubmit}
										disabled={pristine || invalid}
										type={'primary'}
										style={{ margin: '0px' }}
									>
										{t('pages:Uložiť')}
									</Button>
								</div>
								<div className={'flex direction-col justify-center'}>
									{
										id && <Popconfirm
											title={i18next.t('pages:Skutočne chcete vymazať záznam?')}
											icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
											cancelText={i18next.t('pages:Zrušiť')}
											okText={i18next.t('pages:Vymazať')}
											onConfirm={(e) => {
												e?.stopPropagation()
												if (id !== null) {
													dispatch(deletePriceOffer(id, () => {
														history.push(t('paths:priceOffers|path'))
													}))
												}
											}}
											onCancel={(e) => e?.stopPropagation()}
											okButtonProps={{
												size: 'small',
												type: 'primary'
											}}
											cancelButtonProps={{
												size: 'small',
												type: 'ghost'
											}}
										>
											<Button
												icon={<DeleteOutlined />}
												style={{ marginLeft: '0px', marginTop: '8px' }}
												type={'primary'}
												onClick={(e) => e.stopPropagation()}
											>
												{t('components:Odstrániť cenovú ponuku')}
											</Button>
										</Popconfirm>
									}

								</div>
							</div>
							<div className={'flex direction-col justify-center'}>
								{priceForm?.jobId == null
									? <Button
										icon={<ArrowRightOutlined />}
										onClick={
											() => {
												if (id !== null) {
													transferOfferToJob(id)
												}
											}
										}
										disabled={ id == null }
										type={'primary'}
										style={{ margin: '0px', marginTop: '16px' }}
									>
										{t('pages:Previesť na Zákazku')}
									</Button>
									: <>
										<Divider style={{ margin: '12px 0' }} />
										<StyledLink to={`${i18next.t('paths:jobDetail|path')}/${priceForm?.jobId}`}
										>{ i18next.t('components:Detail zákazky') } <b>{priceForm?.address}</b></StyledLink>
									</>
								}
								{id != null && (
									<>
										<Divider style={{ margin: '12px 0' }} />
										<div
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center'
											}}
											className={'font-bold'}
										>
											{t('components:Ostatné cenové ponuky')}
											<Popconfirm
												title={i18next.t('components:Naozaj chcete duplikovať cenovú ponuku?')}
												icon={<QuestionCircleOutlined style={{ color: 'blue' }} />}
												cancelText={i18next.t('pages:Zrušiť')}
												okText={i18next.t('components:Duplikovať')}
												onConfirm={(e) => {
													e?.stopPropagation()
													if (id !== null) { duplicatePriceOffer(id) }
												}}
												onCancel={(e) => e?.stopPropagation()}
												disabled={isDuplicateDisabled}
											>
												<PlusOutlined
													style={isDuplicateDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : { cursor: 'pointer' }}
												/>
											</Popconfirm>
										</div>
										{allPriceOffers.slice(0, 3).map((priceOfferItem) => {
											const isCurrent = Number(priceOfferItem.id) === Number(id)
											return (
												<div
													key={priceOfferItem.id}
													className={'mt-2'}
													style={{
														cursor: isCurrent ? 'default' : 'pointer',
														border: isCurrent ? '1px solid #fa8c16' : undefined,
														borderRadius: isCurrent ? 6 : undefined,
														padding: isCurrent ? '6px 8px' : undefined
													}}
													onClick={() => {
														if (!isCurrent) { history.push(`${t('paths:priceOfferDetail|path')}/${priceOfferItem.id}`) }
													}}
												>
													<div className={'flex flex-row justify-between items-center'}>
														<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
															<Tag color={translatePriceOfferStatusColor(priceOfferItem.status)}>
																{translatePriceOfferStatusToSk(priceOfferItem.status)}
															</Tag>
															{isCurrent && <Tag color={'orange'}>{t('components:Aktuálna')}</Tag>}
														</div>
														<span>{priceOfferItem.price} €</span>
													</div>
													<div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12 }}>
														{dayjs(priceOfferItem.createdAt).format(DEFAULT_DATE_WITH_TIME_FORMAT)}
													</div>
												</div>
											)
										})}
										{otherPriceOffers.length > 0 && (
											<Button
												type={'link'}
												onClick={() => setIsOtherPriceOffersDrawerVisible(true)}
												style={{ padding: 0, marginTop: 8 }}
											>
												{t('components:Zobraziť všetky')} ({allPriceOffers.length})
											</Button>
										)}
									</>
								)}
							</div>
						</div>
					</Col>
				</Row>
				<Row gutter={16}>
					<Col span={18} className={'mt-4'}>
						<Col span={24} className={'grid'}>
							<div className={'flex direction-col justify-start main-content'}>
								<h1>{t('pages:Cenová ponuka')}</h1>
								<Field
									name={ 'priceOfferFirmHeaderID' }
									component={ CustomOptionsSelectField }
									label={t('loc:Hlavička firmy')}
									placeholder={t('loc:Vyber hlavičku firmy')}
									options={ map(priceOfferOptions, (option) => (
										<Option
											key={ option.value }
											value={ option.value }
										>
											{ option.label }
										</Option>
									)) }
									size={ 'large' }
									allowClear
									showSearch
									filterOption={ (input: any, option: any) => option.children.toLowerCase()
										.indexOf(input.toLowerCase()) >= 0 }
								/>
								<Field
									component={InputField}
									label={t('components:Poznámka')}
									placeholder={t('components:Poznámka')}
									name={'comment'}
									size={'large'}
									type={'text'}
								/>
							</div>
							<div className={'flex direction-col justify-start main-content mt-4'}>
								<h1>{t('pages:Charakteristiky objektu')}</h1>
								<Field
									component={InputField}
									label={t('components:Ulica')}
									placeholder={t('components:Ulica')}
									name={'address'}
									size={'large'}
									type={'text'}
									required
								/>
								<Field
									name={'housingAssociationID'}
									component={CustomOptionsSelectField}
									label={t('components:Bytové družstvo')}
									placeholder={t('components:Vyber bytové družstvo')}
									options={map(housingAssociationOptions, (option) => (
										<Option
											key={option.value}
											value={option.value}
										>
											{option.label}
										</Option>
									))}
									size={'large'}
									allowClear
									showSearch
									filterOption={(input: any, option: any) => option.children.toLowerCase()
										.indexOf(input.toLowerCase()) >= 0}
									dropdownRender={(menu: any) => (
										<>
											{menu}
											<Divider style={{ margin: '4px 0' }} />
											<div style={{ padding: '4px 8px' }}>
												<Button
													type={'link'}
													icon={<PlusOutlined />}
													onMouseDown={(e) => e.preventDefault()}
													onClick={() => setIsHousingAssociationModalVisible(true)}
												>
													{t('components:Vytvoriť bytové družstvo')}
												</Button>
											</div>
										</>
									)}
								/>
								<Field
									component={InputField}
									label={t('components:Názov bytového družstva (legacy — kontrola migrácie)')}
									placeholder={t('components:Názov bytového družstva')}
									name={'nameOfHousingAssociation'}
									size={'large'}
									type={'text'}
									disabled
								/>
								<Field
									component={InputField}
									label={t('components:Počet poschodí')}
									placeholder={t('components:Počet poschodí')}
									name={'floors'}
									size={'large'}
									type={'number'}
								/>
								<Field
									component={InputField}
									label={t('components:Počet bytov')}
									placeholder={t('components:Počet bytov')}
									name={'flats'}
									size={'large'}
									type={'number'}
								/>
							</div>
						</Col>
						<Col span={24} className={'grid mt-4'}>
							<div className={'flex direction-col justify-start main-content'}>
								<h1>{t('pages:Klient')}</h1>
								<Field
									component={InputField}
									label={t('components:Meno klienta')}
									placeholder={t('components:Meno klienta')}
									name={'nameOfTrustee'}
									size={'large'}
									type={'text'}
								/>
								<Field
									component={InputField}
									label={t('components:Telefónne číslo')}
									placeholder={t('components:Telefónne číslo')}
									name={'phoneOfTrustee'}
									size={'large'}
									type={'text'}
								/>
								<Field
									component={InputField}
									label={t('components:Email na klienta')}
									placeholder={t('components:Email na klienta')}
									name={'emailOfTrustee'}
									size={'large'}
									type={'text'}
								/>
							</div>
						</Col>
						<Col span={24} className={'grid mt-4'}>
							<div className={'flex direction-col justify-start main-content'}>
								<h1>{t('pages:Iná firma')}</h1>
								<Field
									component={InputField}
									label={t('components:Aká firma upratovala doteraz')}
									placeholder={t('components:Aká firma upratovala doteraz')}
									name={'nameOfFirmWhichCleanedUpToNow'}
									size={'large'}
									type={'text'}
								/>
								<Field
									component={InputField}
									label={t('components:Akú ste mali cenu doteraz ? (eur) ')}
									placeholder={t('components:Akú ste mali cenu doteraz ? (eur) ')}
									name={'pricePaidForCleaningUpToNow'}
									size={'large'}
									type={'number'}
								/>
							</div>
						</Col>
						<Col span={24} className={'grid mt-4'}>
							<div className={'flex direction-col justify-start main-content'}>
								<h1>{t('pages:Charekteristiky zákazky')}</h1>
								<Field
									component={SwitchField}
									label={t('components:Miestnosť pre upratovačky')}
									placeholder={t('components:Miestnosť pre upratovačky')}
									name={'isThereRoomForCleaningService'}
									size={'large'}
									type={'text'}
								/>
								<Field
									component={SelectField}
									label={t('components:Miestnosť kde je voda')}
									placeholder={t('components:Miestnosť kde je voda')}
									name={'placeWithWater'}
									options={placeWithWaterOptions}
									size={'large'}
									type={'text'}
								/>
								<Field
									component={SelectField}
									label={t('components:Vodu možno vylievať')}
									placeholder={t('components:Vodu možno vylievať')}
									name={'whereCanWaterBePoured'}
									size={'large'}
									type={'text'}
									options={placesToSpillWaterOptions}
								/>
							</div>
						</Col>
						<Col span={24} className={'grid mt-4'}>
							<div className={'flex direction-col justify-start main-content'}>
								<h1>{t('pages:Kontaktná firma')}</h1>
								<Field
									component={DateField}
									label={t('components:Kedy sa ozvať')}
									placeholder={t('components:Kedy sa ozvať')}
									name={'whenToCall'}
									size={'large'}
									type={'text'}
								/>
								<label className={'mt-2'}>{t('components:Pripomienky (e-mail)')}</label>
								<FieldArray
									name={'reminders'}
									component={PriceOfferRemindersFields as any}
									props={{ defaultRecipientUserID: currentUserId }}
								/>
								<Field
									component={DateField}
									label={t('components:Kedy bude schôdza')}
									placeholder={t('components:Kedy bude schôdza')}
									name={'dateOfHouseAssociationMeeting'}
									size={'large'}
									type={'text'}
								/>
							</div>
						</Col>
						<Col span={24} className={'grid mt-4'}>
							<div className={'flex direction-col justify-start main-content'}>
								<h1>{t('pages:Naša ponuka')}</h1>
								<Field
									placeholder={t('components:Cena za byt / mesiac (€)')}
									name={'price'}
									component={InputNumberField}
									label={t('components:Cena za byt / mesiac (€)')}
									size={'large'}
									type={'number'}
								/>
								<Field
									placeholder={t('components:Uznesenie je súčasťou ponuky')}
									name={'isUznesenieIsPartOfPriceOffer'}
									component={SwitchField}
									label={t('components:Uznesenie je súčasťou ponuky')}
									size={'large'}
									type={'text'}
								/>
								<Field
									placeholder={t('components:Inflačná doložka')}
									name={'isInflationClauseIsPartOfPriceOffer'}
									component={SwitchField}
									label={t('components:Inflačná doložka')}
									size={'large'}
									type={'text'}
									confirmChange={(newChecked: boolean) => {
										const successfullySentCount = sentEmails.filter((sentEmail) => sentEmail.successfullySent).length
										if (!newChecked && successfullySentCount <= 1) {
											Modal.warning({
												title: t('components:Inflačná doložka'),
												content: t('components:Inflačná doložka musí byť v prvej cenovej ponuke vždy aktívna. Vypnúť ju je možné až po odoslaní prvej cenovej ponuky.')
											})
											return false
										}
										return true
									}}
								/>
							</div>
						</Col>
					</Col>
					<Col span={6}>
						{map(selectedFrequencies, (frequency) => (
							<FrequencyJobsField
								title={t('components:Zazmlúvnené práce')}
								frequency={frequency}
							/>
						))}
						<Col span={24} className={'grid mt-4'}>
							<div className={'flex direction-col justify-start main-content'}>
								<Row justify={'center'}>
									<Col >
										<h3 className="text-center">{t('components:Pridať frequenciu')}</h3>
										<h5 className="text-center">{t('components:napr. (mesačná, štvrťročná, polročná, ročná)')}</h5>
										<div className={'w-full flex justify-center items-center'}>
											<Button
												type="primary"
												shape="circle"
												size={'large'}
												onClick={() => setIsModalVisible(true)}
											>
												<PlusCircleOutlined />
											</Button>
										</div>
									</Col>
								</Row>
							</div>
						</Col>
					</Col>
				</Row>
			</Form>
			<ConfirmationModal
				filterUsedFrequency={selectedFrequencies}
				visible={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				handleOk={(frequency) => {
					setSelectedFrequencies(Array.from(new Set([...selectedFrequencies, frequency])))
					setIsModalVisible(false)
				}}
			/>
			<EmailPickerModal
				initEmail={detail.data?.emailOfTrustee ? [detail.data?.emailOfTrustee] : []}
				visible={isModalEmailPickerVisible}
				isInflationClause={isInflationClauseActive}
				documents={inflationClauseDocuments}
				onCancel={() => setModalEmailPickerVisible(false)}
				handleOk={(emails, inflationClauseDocumentSlot) => {
					if (id !== null) { sendPriceOffer(id, emails, inflationClauseDocumentSlot) }
					setModalEmailPickerVisible(false)
				}}
			/>
			<HousingAssociationModal
				visible={isHousingAssociationModalVisible}
				title={t('components:Vytvoriť bytové družstvo')}
				confirmLoading={isCreatingHousingAssociation}
				onCancel={() => setIsHousingAssociationModalVisible(false)}
				onSubmit={handleCreateHousingAssociation}
			/>
			<SentEmailsDrawer
				sentEmails={sentEmails}
				open={isSentEmailsDrawerVisible}
				onClose={() => setIsSentEmailsDrawerVisible(false)}
			/>
			<OtherPriceOffersDrawer
				otherPriceOffers={allPriceOffers}
				currentPriceOfferId={id}
				open={isOtherPriceOffersDrawerVisible}
				onClose={() => setIsOtherPriceOffersDrawerVisible(false)}
				onCreateNew={() => { if (id !== null) { duplicatePriceOffer(id) } }}
				onSelect={(siblingId) => history.push(`${t('paths:priceOfferDetail|path')}/${siblingId}`)}
				createDisabled={isDuplicateDisabled}
			/>
		</>
	)
}

const form = reduxForm<IPriceOfferForm, ComponentProps>({
	form: FORMS.PRICE_OFFER_FORM,
	forceUnregisterOnUnmount: true,
	touchOnChange: true,
	destroyOnUnmount: true,
	validate: validateForm
})(PriceOfferForm)

export default form
